import React, { useState, useEffect } from 'react';
import { analyticsAPI, compatAPI } from '../../utils/api';
import useReleaseFilter from '../../hooks/useReleaseFilter';
import ReleaseFilterBar from '../../components/ReleaseFilterBar';

const fmt = (n) => {
    if (n == null) return '0';
    return Number(n).toLocaleString();
};

const pct = (n, d) => {
    if (!d) return '0%';
    return (n / d * 100).toFixed(1) + '%';
};

// Simple inline pie chart using SVG
function PieChart({ slices, size = 120 }) {
    const total = slices.reduce((s, sl) => s + (sl.value || 0), 0);
    if (!total) return <div className="text-gray-400 text-sm">No data</div>;
    const r = size / 2 - 4;
    const cx = size / 2;
    const cy = size / 2;
    let cumAngle = -Math.PI / 2;

    const paths = slices.filter(s => s.value > 0).map((sl, i) => {
        const angle = (sl.value / total) * 2 * Math.PI;
        const x1 = cx + r * Math.cos(cumAngle);
        const y1 = cy + r * Math.sin(cumAngle);
        cumAngle += angle;
        const x2 = cx + r * Math.cos(cumAngle);
        const y2 = cy + r * Math.sin(cumAngle);
        const largeArc = angle > Math.PI ? 1 : 0;
        const d = `M${cx},${cy} L${x1},${y1} A${r},${r} 0 ${largeArc} 1 ${x2},${y2} Z`;
        return <path key={i} d={d} fill={sl.color} stroke="white" strokeWidth="1" />;
    });

    return (
        <div className="flex items-center gap-4">
            <svg width={size} height={size}>{paths}</svg>
            <div className="flex flex-col gap-1">
                {slices.filter(s => s.value > 0).map((sl, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                        <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: sl.color }} />
                        <span className="text-gray-700">{sl.label}: {fmt(sl.value)} ({pct(sl.value, total)})</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

// Simple bar chart using CSS
function BarChart({ bars, maxWidth = 200 }) {
    const maxVal = Math.max(...bars.map(b => b.value || 0), 1);
    return (
        <div className="flex flex-col gap-2">
            {bars.map((b, i) => (
                <div key={i} className="flex items-center gap-2">
                    <span className="text-xs text-gray-600 w-24 truncate" title={b.label}>{b.label}</span>
                    <div className="flex-1 bg-gray-100 rounded-full h-5 relative" style={{ maxWidth }}>
                        <div
                            className="h-5 rounded-full"
                            style={{
                                width: `${Math.max((b.value / maxVal) * 100, 2)}%`,
                                backgroundColor: b.color || '#667eea',
                            }}
                        />
                    </div>
                    <span className="text-xs font-semibold text-gray-700 w-16 text-right">{typeof b.display === 'string' ? b.display : fmt(b.value)}</span>
                </div>
            ))}
        </div>
    );
}

// Gauge badge
function GaugeBadge({ value, label, color }) {
    const bgMap = {
        green: 'bg-green-100 text-green-800',
        red: 'bg-red-100 text-red-800',
        yellow: 'bg-yellow-100 text-yellow-800',
        blue: 'bg-blue-100 text-blue-800',
        purple: 'bg-purple-100 text-purple-800',
        gray: 'bg-gray-100 text-gray-800',
    };
    return (
        <div className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold ${bgMap[color] || bgMap.gray}`}>
            {label}: {value}
        </div>
    );
}

// Section wrapper
function Section({ title, children, id }) {
    return (
        <div id={id} className="mb-10">
            <h3 className="text-lg font-bold text-gray-900 mb-4 border-b pb-2">{title}</h3>
            {children}
        </div>
    );
}

// Card wrapper
function Card({ title, children, className = '' }) {
    return (
        <div className={`bg-white rounded-lg shadow-md p-6 ${className}`}>
            {title && <h4 className="text-sm font-bold text-gray-700 mb-3 uppercase tracking-wide">{title}</h4>}
            {children}
        </div>
    );
}

// Data table
function DataTable({ columns, rows }) {
    if (!rows || rows.length === 0) return <div className="text-gray-400 text-sm">No data</div>;
    return (
        <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                    <tr>
                        {columns.map((col, i) => (
                            <th key={i} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{col.label}</th>
                        ))}
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                    {rows.map((row, ri) => (
                        <tr key={ri} className="hover:bg-gray-50">
                            {columns.map((col, ci) => (
                                <td key={ci} className="px-4 py-2 text-sm text-gray-900">
                                    {col.render ? col.render(row[col.key], row) : (row[col.key] ?? '-')}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

const TABS = [
    { id: 'cc-compat', label: 'CC Compat Mode' },
    { id: 'connection-dialogs', label: 'Connection Dialogs' },
    { id: 'dms-transcode', label: 'DMS Transcode' },
    { id: 'dlna-transcode', label: 'DLNA Transcode' },
    { id: 'transcode-offer', label: 'Transcode Offer' },
    { id: 'browser-cast', label: 'Browser Cast' },
];

export default function PipelineAnalyticsPage() {
    const [activeTab, setActiveTab] = useState('cc-compat');
    const [loading, setLoading] = useState(true);
    const [appVersions, setAppVersions] = useState([]);
    const [currentVersion, setCurrentVersion] = useState(null);
    const filter = useReleaseFilter({ days: 30 });

    const [ccCompat, setCcCompat] = useState(null);
    const [connDialogs, setConnDialogs] = useState(null);
    const [dmsTranscode, setDmsTranscode] = useState(null);
    const [dlnaTranscode, setDlnaTranscode] = useState(null);
    const [transcodeOffer, setTranscodeOffer] = useState(null);
    const [browserCast, setBrowserCast] = useState(null);

    useEffect(() => {
        compatAPI.getBuildsSeen(filter.days).then(r => {
            setAppVersions(r.data?.app_versions || []);
            setCurrentVersion(r.data?.current_app_version_name || null);
        }).catch(() => {});
    }, [filter.days]);

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter.queryParams.release_op, filter.queryParams.release_value, filter.queryParams.days, filter.queryParams.layer]);

    const loadData = async () => {
        setLoading(true);
        try {
            // Pass the release filter through to all five sub-endpoints.
            // The page does Promise.all so they all observe the same scope.
            const params = filter.queryParams;
            const days = filter.days;
            const [cc, cd, dms, dlna, offer, bc] = await Promise.all([
                analyticsAPI.getPipelineCcCompat(days, params).catch(() => ({ data: null })),
                analyticsAPI.getPipelineConnectionDialogs(days, params).catch(() => ({ data: null })),
                analyticsAPI.getPipelineDmsTranscode(days, params).catch(() => ({ data: null })),
                analyticsAPI.getPipelineDlnaTranscode(days, params).catch(() => ({ data: null })),
                analyticsAPI.getPipelineTranscodeOffer(days, params).catch(() => ({ data: null })),
                analyticsAPI.getPipelineBrowserCast(days, params).catch(() => ({ data: null })),
            ]);
            // Backend returns flat shapes (e.g. total_activations, hw_transcode_attempts).
            // The renderers below were authored against an older nested shape
            // (activation, transcode, reliability, ...). Adapt here so the
            // existing render code keeps working without semantic changes.
            setCcCompat(normalizeCcCompat(cc.data));
            setConnDialogs(normalizeConnDialogs(cd.data));
            setDmsTranscode(normalizeDmsTranscode(dms.data));
            setDlnaTranscode(normalizeDlnaTranscode(dlna.data));
            setTranscodeOffer(normalizeTranscodeOffer(offer.data));
            setBrowserCast(bc.data || null);
        } catch (error) {
            console.error('Error loading pipeline analytics:', error);
        } finally {
            setLoading(false);
        }
    };

    // ---- response-shape adapters ----
    function normalizeCcCompat(d) {
        if (!d || typeof d !== 'object') return null;
        // Already-nested shape: pass through. Strict check on a known
        // inner key so we never short-circuit on a flat response that
        // happens to use one of these as its own field name.
        if (d.activation && typeof d.activation === 'object' && d.activation.total_activations != null) return d;
        const hw = d.hw_transcode_attempts || {};
        const vlc = d.vlc_hls_attempts || {};
        // Build the failing_formats grid from top_failing_codecs / top_failing_resolutions.
        const codecs = Array.isArray(d.top_failing_codecs) ? d.top_failing_codecs : [];
        const resolutions = Array.isArray(d.top_failing_resolutions) ? d.top_failing_resolutions : [];
        const failingFormats = [];
        const cap = Math.max(codecs.length, resolutions.length);
        for (let i = 0; i < cap; i++) {
            failingFormats.push({
                codec: codecs[i]?.video_codec || codecs[i]?.codec || '-',
                resolution: resolutions[i]?.resolution || '-',
                count: Number(codecs[i]?.count ?? resolutions[i]?.count ?? 0),
                hdr: false,
                ten_bit: false,
            });
        }
        return {
            activation: {
                total_activations: Number(d.total_activations) || 0,
                compat_mode_toggles: Number(d.compat_mode_toggle_rate) || 0,
            },
            transcode: {
                hw_success: Number(hw.success) || 0,
                hw_fail: Number(hw.fail) || 0,
                vlc_hls_success: Number(vlc.success) || 0,
                vlc_hls_fail: Number(vlc.fail) || 0,
            },
            reliability: {
                total: 100,
                successes: Number(d.session_start_success_rate) || 0,
                avg_retries: Number(d.avg_retries_before_success) || 0,
                avg_connect_time_ms: Number(d.avg_time_to_connect_ms) || 0,
            },
            failing_formats: failingFormats,
            user_choices: Array.isArray(d.format_dialog_choices)
                ? d.format_dialog_choices.map(c => ({ choice: c.selection || c.choice, count: Number(c.count) || 0 }))
                : [],
            audio_tap: {
                total: 100,
                successes: Number(d.audio_tap_success_rate) || 0,
            },
            hdr_failure_rate: Number(d.hdr_10bit_failure_rate) || 0,
        };
    }

    function normalizeConnDialogs(d) {
        if (!d || typeof d !== 'object') return null;
        // Detect already-nested shape strictly. The flat backend response
        // has `step_timing` as an OBJECT keyed by step name; the nested
        // render-side shape has `metrics.total_shown` set. Without the
        // strict check, every flat response was returned untouched and
        // every render card showed 0.
        if (d.metrics && typeof d.metrics === 'object' && d.metrics.total_shown != null) return d;
        const total = Number(d.total_dialogs_shown) || 0;
        const succ = Math.round(total * (Number(d.success_rate) || 0) / 100);
        const cancel = Math.round(total * (Number(d.cancel_rate) || 0) / 100);
        const retry = Math.round(total * (Number(d.retry_rate) || 0) / 100);
        const stRow = d.step_timing || {};
        const stepTiming = Object.entries(stRow)
            .filter(([, v]) => v != null)
            .map(([k, v]) => ({ step: k, avg_duration_ms: Number(v) || 0 }));
        return {
            metrics: { total_shown: total, success_count: succ, cancel_count: cancel, retry_count: retry },
            step_timing: stepTiming,
            overlap: { count: Number(d.overlap_count) || 0 },
            compat_restarts: { count: Number(d.compat_restart_count) || 0 },
        };
    }

    function normalizeDmsTranscode(d) {
        if (!d || typeof d !== 'object') return null;
        if (d.requests && typeof d.requests === 'object' && d.requests.total != null) return d;
        const totalReq = Number(d.total_stream_requests) || 0;
        const transcodeNeeded = Math.round(totalReq * (Number(d.transcode_needed_rate) || 0) / 100);
        const codecs = Array.isArray(d.top_source_codecs) ? d.top_source_codecs : [];
        const resolutions = Array.isArray(d.top_source_resolutions) ? d.top_source_resolutions : [];
        const formats = [];
        const cap = Math.max(codecs.length, resolutions.length);
        for (let i = 0; i < cap; i++) {
            formats.push({
                codec: codecs[i]?.source_codec || codecs[i]?.codec || '-',
                resolution: resolutions[i]?.source_resolution || resolutions[i]?.resolution || '-',
                count: Number(codecs[i]?.count ?? resolutions[i]?.count ?? 0),
            });
        }
        return {
            requests: { total: totalReq, transcode_needed: transcodeNeeded },
            success: {
                success_count: Number(d.transcode_completed) || 0,
                fail_count: Number(d.transcode_failed) || 0,
            },
            performance: {
                avg_encoding_fps: Number(d.avg_encoding_fps) || 0,
                avg_duration_ms: Number(d.avg_transcode_duration_ms) || 0,
            },
            source_formats: formats,
        };
    }

    function normalizeDlnaTranscode(d) {
        if (!d || typeof d !== 'object') return null;
        if (d.playback && typeof d.playback === 'object' && d.playback.total_started != null) return d;
        const lt = d.live_transcode_count || {};
        const codecs = Array.isArray(d.top_incompatible_codecs) ? d.top_incompatible_codecs : [];
        const resolutions = Array.isArray(d.top_incompatible_resolutions) ? d.top_incompatible_resolutions : [];
        const formats = [];
        const cap = Math.max(codecs.length, resolutions.length);
        for (let i = 0; i < cap; i++) {
            formats.push({
                codec: codecs[i]?.video_codec || codecs[i]?.codec || '-',
                resolution: resolutions[i]?.resolution || '-',
                count: Number(codecs[i]?.count ?? resolutions[i]?.count ?? 0),
                hdr: false,
            });
        }
        return {
            playback: {
                total_started: Number(d.total_playback_started) || 0,
                format_incompatible: Number(d.format_incompatible_count) || 0,
            },
            live_transcode: {
                count: Number(lt.started) || 0,
                avg_fps: Number(d.live_transcode_avg_fps) || 0,
                avg_bytes_served: Number(d.live_transcode_avg_bytes_served) || 0,
            },
            failing_formats: formats,
            offline_triggers: Array.isArray(d.offline_transcode_triggered)
                ? d.offline_transcode_triggered.map(t => ({ reason: t.trigger_reason, count: Number(t.total) || 0 }))
                : [],
            hdr_rate: Number(d.hdr_failure_rate) || 0,
        };
    }

    function normalizeTranscodeOffer(d) {
        if (!d || typeof d !== 'object') return null;
        if (d.selections && Array.isArray(d.selections) && d.total_offers != null) return d;
        const cl = d.cloud_vs_local_ratio || {};
        return {
            total_offers: Number(d.total_offers_shown) || 0,
            selections: Array.isArray(d.selection_breakdown)
                ? d.selection_breakdown.map(s => ({ choice: s.selection || s.choice, count: Number(s.count) || 0 }))
                : [],
            avg_decision_time_ms: Number(d.avg_time_to_choose_ms) || 0,
            cloud_vs_local: {
                cloud: Number(cl.cloud) || 0,
                local: Number(cl.local) || 0,
            },
        };
    }

    const renderCcCompat = () => {
        const d = ccCompat || {};
        const activation = d.activation || {};
        const transcode = d.transcode || {};
        const reliability = d.reliability || {};
        const failingFormats = d.failing_formats || [];
        const userChoices = d.user_choices || [];
        const audioTap = d.audio_tap || {};

        return (
            <Section title="CC Compat Mode" id="cc-compat">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                    {/* Activation Rate */}
                    <Card title="Activation Rate">
                        <div className="flex flex-col gap-2">
                            <div className="text-3xl font-bold text-gray-900">{fmt(activation.total_activations)}</div>
                            <div className="text-sm text-gray-500">Total activations</div>
                            <GaugeBadge
                                value={pct(activation.compat_mode_toggles, activation.total_activations)}
                                label="Toggle rate"
                                color={parseFloat(pct(activation.compat_mode_toggles, activation.total_activations)) > 50 ? 'yellow' : 'green'}
                            />
                        </div>
                    </Card>

                    {/* Transcode Pipeline */}
                    <Card title="Transcode Pipeline">
                        <div className="mb-3">
                            <p className="text-xs text-gray-500 mb-1">HW Encoder</p>
                            <PieChart slices={[
                                { label: 'Success', value: transcode.hw_success || 0, color: '#22c55e' },
                                { label: 'Fail', value: transcode.hw_fail || 0, color: '#ef4444' },
                            ]} size={90} />
                        </div>
                        <div>
                            <p className="text-xs text-gray-500 mb-1">VLC HLS</p>
                            <PieChart slices={[
                                { label: 'Success', value: transcode.vlc_hls_success || 0, color: '#3b82f6' },
                                { label: 'Fail', value: transcode.vlc_hls_fail || 0, color: '#f97316' },
                            ]} size={90} />
                        </div>
                    </Card>

                    {/* Session Reliability */}
                    <Card title="Session Reliability">
                        <div className="flex flex-col gap-2">
                            <GaugeBadge
                                value={pct(reliability.successes, reliability.total)}
                                label="Success rate"
                                color={parseFloat(pct(reliability.successes, reliability.total)) >= 80 ? 'green' : 'red'}
                            />
                            <div className="text-sm text-gray-600">
                                Avg retries: <span className="font-semibold">{reliability.avg_retries != null ? Number(reliability.avg_retries).toFixed(1) : '-'}</span>
                            </div>
                            <div className="text-sm text-gray-600">
                                Avg connect: <span className="font-semibold">{reliability.avg_connect_time_ms != null ? Math.round(reliability.avg_connect_time_ms) + 'ms' : '-'}</span>
                            </div>
                        </div>
                    </Card>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Failing Formats */}
                    <Card title="Failing Formats">
                        <DataTable
                            columns={[
                                { key: 'codec', label: 'Codec' },
                                { key: 'resolution', label: 'Resolution' },
                                { key: 'count', label: 'Count', render: (v) => <span className="font-semibold">{fmt(v)}</span> },
                                { key: 'hdr', label: 'HDR', render: (v) => v ? <span className="text-yellow-600 font-semibold">Yes</span> : 'No' },
                                { key: 'ten_bit', label: '10-bit', render: (v) => v ? <span className="text-yellow-600 font-semibold">Yes</span> : 'No' },
                            ]}
                            rows={failingFormats}
                        />
                        {d.hdr_failure_rate != null && (
                            <div className="mt-2 text-sm text-gray-600">
                                HDR failure rate: <span className="font-semibold text-yellow-700">{pct(d.hdr_failure_rate, 1)}</span>
                            </div>
                        )}
                    </Card>

                    {/* User Choices */}
                    <Card title="User Choices">
                        <PieChart slices={
                            userChoices.map((c, i) => ({
                                label: c.choice || c.label || 'Unknown',
                                value: c.count || 0,
                                color: ['#667eea', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'][i % 6],
                            }))
                        } size={120} />
                    </Card>
                </div>

                {/* Audio Tap */}
                <div className="mt-6">
                    <Card title="Audio Tap">
                        <GaugeBadge
                            value={pct(audioTap.successes, audioTap.total)}
                            label="Success rate"
                            color={parseFloat(pct(audioTap.successes, audioTap.total)) >= 80 ? 'green' : 'red'}
                        />
                        <span className="ml-3 text-sm text-gray-500">
                            {fmt(audioTap.successes)} / {fmt(audioTap.total)} attempts
                        </span>
                    </Card>
                </div>
            </Section>
        );
    };

    const renderConnectionDialogs = () => {
        const d = connDialogs || {};
        const metrics = d.metrics || {};
        const stepTiming = d.step_timing || [];
        const overlap = d.overlap || {};
        const compatRestarts = d.compat_restarts || {};

        return (
            <Section title="Connection Dialogs" id="connection-dialogs">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                    {/* Dialog Metrics */}
                    <Card title="Dialog Metrics">
                        <div className="text-3xl font-bold text-gray-900 mb-2">{fmt(metrics.total_shown)}</div>
                        <div className="text-sm text-gray-500 mb-3">Total dialogs shown</div>
                        <BarChart bars={[
                            { label: 'Success', value: metrics.success_count || 0, color: '#22c55e' },
                            { label: 'Cancel', value: metrics.cancel_count || 0, color: '#ef4444' },
                            { label: 'Retry', value: metrics.retry_count || 0, color: '#f59e0b' },
                        ]} />
                        <div className="mt-2 text-xs text-gray-500">
                            Success: {pct(metrics.success_count, metrics.total_shown)} |
                            Cancel: {pct(metrics.cancel_count, metrics.total_shown)} |
                            Retry: {pct(metrics.retry_count, metrics.total_shown)}
                        </div>
                    </Card>

                    {/* Step Timing */}
                    <Card title="Step Timing (avg ms)">
                        <BarChart bars={
                            stepTiming.map((s) => ({
                                label: s.step || s.name || 'Unknown',
                                value: s.avg_duration_ms || 0,
                                display: Math.round(s.avg_duration_ms || 0) + 'ms',
                                color: '#667eea',
                            }))
                        } maxWidth={180} />
                    </Card>

                    {/* Overlap Detection & Compat Restarts */}
                    <Card title="Overlap & Restarts">
                        <div className="flex flex-col gap-4">
                            <div>
                                <div className="text-sm text-gray-600 mb-1">Overlapping Dialogs</div>
                                <div className={`text-2xl font-bold ${(overlap.count || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                    {fmt(overlap.count)}
                                </div>
                                {(overlap.count || 0) > 0 && (
                                    <div className="mt-1 bg-red-50 border-l-4 border-red-500 p-2 rounded-r text-xs text-red-700">
                                        Warning: overlapping dialogs detected
                                    </div>
                                )}
                            </div>
                            <div>
                                <div className="text-sm text-gray-600 mb-1">Compat Restarts</div>
                                <div className="text-2xl font-bold text-gray-900">{fmt(compatRestarts.count)}</div>
                            </div>
                        </div>
                    </Card>
                </div>
            </Section>
        );
    };

    const renderDmsTranscode = () => {
        const d = dmsTranscode || {};
        const requests = d.requests || {};
        const success = d.success || {};
        const performance = d.performance || {};
        const sourceFormats = d.source_formats || [];

        return (
            <Section title="DMS Transcode" id="dms-transcode">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                    {/* Stream Requests */}
                    <Card title="Stream Requests">
                        <div className="text-3xl font-bold text-gray-900">{fmt(requests.total)}</div>
                        <div className="text-sm text-gray-500 mb-2">Total requests</div>
                        <GaugeBadge
                            value={pct(requests.transcode_needed, requests.total)}
                            label="Transcode needed"
                            color="blue"
                        />
                    </Card>

                    {/* Transcode Success */}
                    <Card title="Transcode Success">
                        <PieChart slices={[
                            { label: 'Success', value: success.success_count || 0, color: '#22c55e' },
                            { label: 'Fail', value: success.fail_count || 0, color: '#ef4444' },
                        ]} size={100} />
                    </Card>

                    {/* Performance */}
                    <Card title="Performance">
                        <div className="flex flex-col gap-2">
                            <div className="text-sm text-gray-600">
                                Avg encoding FPS: <span className="text-xl font-bold text-gray-900">{performance.avg_encoding_fps != null ? Number(performance.avg_encoding_fps).toFixed(1) : '-'}</span>
                            </div>
                            <div className="text-sm text-gray-600">
                                Avg duration: <span className="text-xl font-bold text-gray-900">{performance.avg_duration_ms != null ? Math.round(performance.avg_duration_ms / 1000) + 's' : '-'}</span>
                            </div>
                        </div>
                    </Card>
                </div>

                {/* Source Formats */}
                <Card title="Source Formats">
                    <DataTable
                        columns={[
                            { key: 'codec', label: 'Codec' },
                            { key: 'resolution', label: 'Resolution' },
                            { key: 'count', label: 'Count', render: (v) => <span className="font-semibold">{fmt(v)}</span> },
                        ]}
                        rows={sourceFormats}
                    />
                </Card>
            </Section>
        );
    };

    const renderDlnaTranscode = () => {
        const d = dlnaTranscode || {};
        const playback = d.playback || {};
        const liveTranscode = d.live_transcode || {};
        const failingFormats = d.failing_formats || [];
        const offlineTriggers = d.offline_triggers || [];

        return (
            <Section title="DLNA Transcode" id="dlna-transcode">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                    {/* Playback */}
                    <Card title="Playback">
                        <div className="text-3xl font-bold text-gray-900">{fmt(playback.total_started)}</div>
                        <div className="text-sm text-gray-500 mb-2">Total started</div>
                        <div className="text-sm text-gray-600">
                            Format incompatible: <span className="font-semibold text-red-600">{fmt(playback.format_incompatible)}</span>
                        </div>
                    </Card>

                    {/* Live Transcode */}
                    <Card title="Live Transcode">
                        <div className="flex flex-col gap-2">
                            <div className="text-2xl font-bold text-gray-900">{fmt(liveTranscode.count)}</div>
                            <div className="text-sm text-gray-500">sessions</div>
                            <div className="text-sm text-gray-600">
                                Avg FPS: <span className="font-semibold">{liveTranscode.avg_fps != null ? Number(liveTranscode.avg_fps).toFixed(1) : '-'}</span>
                            </div>
                            <div className="text-sm text-gray-600">
                                Avg bytes served: <span className="font-semibold">
                                    {liveTranscode.avg_bytes_served != null
                                        ? (liveTranscode.avg_bytes_served / 1024 / 1024).toFixed(1) + ' MB'
                                        : '-'}
                                </span>
                            </div>
                        </div>
                    </Card>

                    {/* Offline Triggers */}
                    <Card title="Offline Triggers">
                        {offlineTriggers.length > 0 ? (
                            <BarChart bars={
                                offlineTriggers.map((t) => ({
                                    label: t.reason || t.trigger || 'Unknown',
                                    value: t.count || 0,
                                    color: '#8b5cf6',
                                }))
                            } />
                        ) : (
                            <div className="text-gray-400 text-sm">No offline triggers</div>
                        )}
                    </Card>
                </div>

                {/* Failing Formats */}
                <Card title="Failing Formats">
                    <DataTable
                        columns={[
                            { key: 'codec', label: 'Codec' },
                            { key: 'resolution', label: 'Resolution' },
                            { key: 'count', label: 'Count', render: (v) => <span className="font-semibold">{fmt(v)}</span> },
                            { key: 'hdr', label: 'HDR', render: (v) => v ? <span className="text-yellow-600 font-semibold">Yes</span> : 'No' },
                        ]}
                        rows={failingFormats}
                    />
                    {d.hdr_rate != null && (
                        <div className="mt-2 text-sm text-gray-600">
                            HDR rate among failures: <span className="font-semibold text-yellow-700">{pct(d.hdr_rate, 1)}</span>
                        </div>
                    )}
                </Card>
            </Section>
        );
    };

    const renderTranscodeOffer = () => {
        const d = transcodeOffer || {};
        const selections = d.selections || [];
        const totalOffers = d.total_offers || 0;
        const decisionTime = d.avg_decision_time_ms;
        const cloudVsLocal = d.cloud_vs_local || {};

        return (
            <Section title="Transcode Offer" id="transcode-offer">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                    {/* Total Offers */}
                    <Card title="Total Offers">
                        <div className="text-3xl font-bold text-gray-900">{fmt(totalOffers)}</div>
                        <div className="text-sm text-gray-500">offers shown</div>
                    </Card>

                    {/* Selection Breakdown */}
                    <Card title="Selection Breakdown">
                        <PieChart slices={
                            selections.map((s, i) => ({
                                label: s.choice || s.label || 'Unknown',
                                value: s.count || 0,
                                color: ['#667eea', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#6366f1'][i % 8],
                            }))
                        } size={120} />
                    </Card>

                    {/* Decision Time & Cloud vs Local */}
                    <Card title="Decision Time & Source">
                        <div className="flex flex-col gap-4">
                            <div>
                                <div className="text-sm text-gray-600 mb-1">Avg time to choose</div>
                                <div className="text-2xl font-bold text-gray-900">
                                    {decisionTime != null ? (decisionTime / 1000).toFixed(1) + 's' : '-'}
                                </div>
                            </div>
                            <div>
                                <div className="text-sm text-gray-600 mb-2">Cloud vs Local</div>
                                {(cloudVsLocal.cloud != null || cloudVsLocal.local != null) ? (
                                    <div className="flex items-center gap-2">
                                        <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden flex">
                                            {cloudVsLocal.cloud > 0 && (
                                                <div
                                                    className="h-6 bg-blue-500 flex items-center justify-center text-white text-xs font-semibold"
                                                    style={{ width: `${pct(cloudVsLocal.cloud, (cloudVsLocal.cloud || 0) + (cloudVsLocal.local || 0))}` }}
                                                >
                                                    Cloud
                                                </div>
                                            )}
                                            {cloudVsLocal.local > 0 && (
                                                <div
                                                    className="h-6 bg-green-500 flex items-center justify-center text-white text-xs font-semibold"
                                                    style={{ width: `${pct(cloudVsLocal.local, (cloudVsLocal.cloud || 0) + (cloudVsLocal.local || 0))}` }}
                                                >
                                                    Local
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-gray-400 text-sm">No data</div>
                                )}
                                <div className="mt-1 text-xs text-gray-500">
                                    Cloud: {fmt(cloudVsLocal.cloud)} | Local: {fmt(cloudVsLocal.local)}
                                </div>
                            </div>
                        </div>
                    </Card>
                </div>
            </Section>
        );
    };

    const renderBrowserCast = () => {
        if (!browserCast) return <div className="text-gray-400">No browser-cast data in window</div>;
        const s = browserCast.summary || {};
        const rg = browserCast.renderer_gone || [];
        const ca = browserCast.cursor_anchor || [];
        const dc = browserCast.disconnects || [];
        const rs = browserCast.relay_state_change || [];
        const fs = browserCast.foreground_service_state || [];
        const re = browserCast.receiver_errors || [];

        // Pick-kind colour map for cursor anchor chart
        const PICK_COLORS = {
            parsed_sync: '#10b981',
            size_heuristic: '#f59e0b',
            any_video: '#ef4444',
            audio_fallback: '#7c3aed',
        };

        // Aggregate did_crash split from renderer_gone rows
        let lmkLikely = 0;
        let crashLikely = 0;
        rg.forEach(r => {
            const count = Number(r.occurrence_count) || 0;
            if (Number(r.did_crash) === 1) crashLikely += count;
            else lmkLikely += count;
        });

        // reason-code → human label
        const REASON_LABELS = {
            2055: '2055 socket-idle (300s timeout)',
            2150: '2150 already-active',
            2155: '2155 other',
            2161: '2161 ended by app',
        };

        return (
            <Section title="Browser Cast Pipeline (v1.12.0.18+)" id="browser-cast">
                <div className="mb-6 flex flex-wrap gap-2">
                    <GaugeBadge label="Renderer gone" value={fmt(s.browser_cast_renderer_gone || 0)} color={lmkLikely > 0 ? 'red' : 'green'} />
                    <GaugeBadge label="Cursor anchors" value={fmt(s.browser_cast_cursor_anchor || 0)} color="blue" />
                    <GaugeBadge label="Disconnects" value={fmt(s.browser_cast_disconnect || 0)} color="yellow" />
                    <GaugeBadge label="Relay resets" value={fmt(s.browser_cast_relay_state_change || 0)} color="purple" />
                    <GaugeBadge label="FG-service evts" value={fmt(s.browser_cast_foreground_service_state || 0)} color="blue" />
                    <GaugeBadge label="Receiver errors" value={fmt(s.browser_cast_receiver_error || 0)} color={re.length > 0 ? 'red' : 'green'} />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                    <Card title="Renderer gone — kill kind">
                        <PieChart slices={[
                            { label: 'LMK / external signal', value: lmkLikely, color: '#ef4444' },
                            { label: 'Internal renderer fault', value: crashLikely, color: '#f59e0b' },
                        ]} />
                        <p className="text-xs text-gray-500 mt-3">
                            External-signal kills are the LMK-during-cast pattern from 20:06:18 (renderer
                            backgrounded with cast active, foreground-service binding missing → process killed).
                        </p>
                    </Card>

                    <Card title="Cursor anchor — pick distribution">
                        {ca.length === 0 ? (
                            <div className="text-gray-400 text-sm">No anchor events</div>
                        ) : (
                            <PieChart slices={ca.map(r => ({
                                label: r.pick_kind || '(unknown)',
                                value: Number(r.anchor_count) || 0,
                                color: PICK_COLORS[r.pick_kind] || '#9ca3af',
                            }))} />
                        )}
                        <p className="text-xs text-gray-500 mt-3">
                            <span className="text-green-600 font-semibold">parsed_sync</span> = fMP4 sync-flag verified.
                            <span className="text-amber-600 font-semibold"> size_heuristic</span> = parse pending.
                            <span className="text-red-600 font-semibold"> any_video</span> = mid-GOP fallback.
                            <span className="text-purple-600 font-semibold"> audio_fallback</span> = no video in ring (TV black).
                        </p>
                    </Card>
                </div>

                <Section title="Renderer Gone — top buckets">
                    <DataTable
                        columns={['Protocol', 'Sink', 'Mode', 'Kill kind', 'did_crash', 'FG bound', 'Trim level', 'Count', 'Avg ms since cast start']}
                        rows={rg.slice(0, 20).map(r => [
                            r.cast_protocol || '-',
                            r.cast_sink || '-',
                            r.cast_mode || '-',
                            r.kill_kind || '-',
                            r.did_crash == null ? '-' : (Number(r.did_crash) ? 'yes' : 'no'),
                            r.foreground_service_bound == null ? '-' : (Number(r.foreground_service_bound) ? 'yes' : 'no'),
                            r.last_trim_memory_level == null ? '-' : String(r.last_trim_memory_level),
                            fmt(r.occurrence_count),
                            r.avg_ms_since_cast_start ? Math.round(r.avg_ms_since_cast_start) + ' ms' : '-',
                        ])} />
                </Section>

                <Section title="Cast SDK Disconnect reason codes">
                    {dc.length === 0 ? (
                        <div className="text-gray-400 text-sm">No disconnects in window</div>
                    ) : (
                        <BarChart bars={dc.slice(0, 15).map(r => ({
                            label: REASON_LABELS[r.reason_code] || (r.reason_code != null ? String(r.reason_code) : (r.reason || '?')),
                            value: Number(r.disconnect_count) || 0,
                            color: r.reason_code === 2055 ? '#ef4444' : '#667eea',
                        }))} />
                    )}
                </Section>

                <Section title="Receiver-side errors (from cast receiver HTML)">
                    <DataTable
                        columns={['Protocol', 'Sink', 'Error class', 'Track', 'Count', 'Avg seg idx']}
                        rows={re.slice(0, 30).map(r => [
                            r.cast_protocol || '-',
                            r.cast_sink || '-',
                            r.error_class || '-',
                            r.track || '-',
                            fmt(r.error_count),
                            r.avg_seg_idx ? Math.round(r.avg_seg_idx) : '-',
                        ])} />
                </Section>

                <Section title="Relay state changes (init-segment teardown impact)">
                    <DataTable
                        columns={['Trigger', 'Kept init segments', 'Count', 'Avg elements dropped', 'Avg bytes dropped']}
                        rows={rs.map(r => [
                            r.trigger_kind || '-',
                            r.kept_init_segments == null ? '-' : (Number(r.kept_init_segments) ? 'yes' : 'no'),
                            fmt(r.event_count),
                            r.avg_elements_dropped ? r.avg_elements_dropped.toFixed(1) : '-',
                            r.avg_bytes_dropped ? Math.round(r.avg_bytes_dropped / 1024) + ' KB' : '-',
                        ])} />
                </Section>

                <Section title="Foreground service state (cast-active / trim-memory)">
                    <DataTable
                        columns={['Trigger', 'Service bound', 'Trim level', 'Was casting', 'Count']}
                        rows={fs.slice(0, 30).map(r => [
                            r.trigger_kind || '-',
                            r.service_bound == null ? '-' : (Number(r.service_bound) ? 'yes' : 'no'),
                            r.trim_memory_level == null ? '-' : String(r.trim_memory_level),
                            r.was_casting == null ? '-' : (Number(r.was_casting) ? 'yes' : 'no'),
                            fmt(r.event_count),
                        ])} />
                    <p className="text-xs text-gray-500 mt-3">
                        Look for <span className="font-mono">trigger=byte_relay_start service_bound=no</span> — that was the
                        pre-v1.12.0.17 bug. Also <span className="font-mono">trigger=on_trim_memory level=80 was_casting=yes</span>
                        — the imminent-LMK state.
                    </p>
                </Section>
            </Section>
        );
    };

    const renderActiveTab = () => {
        switch (activeTab) {
            case 'cc-compat': return renderCcCompat();
            case 'connection-dialogs': return renderConnectionDialogs();
            case 'dms-transcode': return renderDmsTranscode();
            case 'dlna-transcode': return renderDlnaTranscode();
            case 'transcode-offer': return renderTranscodeOffer();
            case 'browser-cast': return renderBrowserCast();
            default: return null;
        }
    };

    if (loading && !ccCompat && !connDialogs && !dmsTranscode && !dlnaTranscode && !transcodeOffer) {
        return <div className="text-gray-400">Loading...</div>;
    }

    return (
        <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Pipeline Analytics</h2>

            <ReleaseFilterBar
                filter={filter}
                appVersions={appVersions}
                currentVersion={currentVersion} />

            {/* Tab Bar */}
            <div className="flex flex-wrap gap-1 mb-6 bg-gray-100 rounded-lg p-1">
                {TABS.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                            activeTab === tab.id
                                ? 'bg-white text-zebcast-purple shadow-sm'
                                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                        }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {loading && (
                <div className="text-center text-gray-400 mb-4">Refreshing data...</div>
            )}

            {renderActiveTab()}
        </div>
    );
}
